import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Image, Video, Music, HardDrive, TrendingUp, FolderOpen } from 'lucide-react';

const MediaAnalytics = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        if (!token) { navigate('/admin/login'); return; }
        const response = await fetch('/api/admin/media-analytics', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          if (response.status === 401) { localStorage.removeItem('admin_token'); navigate('/admin/login'); }
          throw new Error('Failed');
        }
        setAnalytics(await response.json());
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [navigate]);

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-party-red mx-auto"></div>
      </div>
    );
  }

  const typeIcons = { image: Image, video: Video, audio: Music };
  const typeColors = { image: 'text-blue-600 bg-blue-50', video: 'text-purple-600 bg-purple-50', audio: 'text-orange-600 bg-orange-50' };
  const byType = analytics?.by_type || {};
  const byCategory = analytics?.by_category || {};
  const trend = analytics?.upload_trend || [];
  const maxTrend = Math.max(...trend.map(t => t.count), 1);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="media-analytics-page">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} data-testid="back-to-dashboard-btn">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t({ en: 'Dashboard', bn: 'ড্যাশবোর্ড' })}
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t({ en: 'Media Analytics', bn: 'মিডিয়া বিশ্লেষণ' })}</h1>
            <p className="text-sm text-gray-500">{t({ en: 'Upload trends, storage usage, and media breakdown', bn: 'আপলোড প্রবণতা, স্টোরেজ ব্যবহার এবং মিডিয়া বিশ্লেষণ' })}</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-5 text-center">
              <div className="w-12 h-12 rounded-full bg-party-red/10 flex items-center justify-center mx-auto mb-3">
                <FolderOpen className="h-6 w-6 text-party-red" />
              </div>
              <div className="text-3xl font-bold text-gray-900">{analytics?.total_items || 0}</div>
              <div className="text-sm text-gray-500">{t({ en: 'Total Media', bn: 'মোট মিডিয়া' })}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5 text-center">
              <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-3">
                <HardDrive className="h-6 w-6 text-blue-600" />
              </div>
              <div className="text-3xl font-bold text-gray-900">{formatBytes(analytics?.total_storage_bytes)}</div>
              <div className="text-sm text-gray-500">{t({ en: 'Storage Used', bn: 'ব্যবহৃত স্টোরেজ' })}</div>
            </CardContent>
          </Card>
          {Object.entries(byType).map(([type, data]) => {
            const Icon = typeIcons[type] || Image;
            const color = typeColors[type] || 'text-gray-600 bg-gray-50';
            return (
              <Card key={type}>
                <CardContent className="p-5 text-center">
                  <div className={`w-12 h-12 rounded-full ${color} flex items-center justify-center mx-auto mb-3`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900">{data.count}</div>
                  <div className="text-sm text-gray-500 capitalize">{type}s</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Upload Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <TrendingUp className="h-5 w-5 text-party-red" />
                {t({ en: 'Upload Trend (30 Days)', bn: 'আপলোড প্রবণতা (৩০ দিন)' })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {trend.length === 0 ? (
                <p className="text-gray-400 text-center py-8">{t({ en: 'No uploads in the last 30 days', bn: 'গত ৩০ দিনে কোনো আপলোড নেই' })}</p>
              ) : (
                <div className="space-y-2">
                  {trend.map((item) => (
                    <div key={item.date} className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 w-20 shrink-0">{item.date.slice(5)}</span>
                      <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-party-red to-party-red/70 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                          style={{ width: `${Math.max((item.count / maxTrend) * 100, 8)}%` }}
                        >
                          <span className="text-xs text-white font-medium">{item.count}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Category Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FolderOpen className="h-5 w-5 text-party-green" />
                {t({ en: 'By Category', bn: 'বিভাগ অনুযায়ী' })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(byCategory).length === 0 ? (
                <p className="text-gray-400 text-center py-8">{t({ en: 'No categories found', bn: 'কোনো বিভাগ পাওয়া যায়নি' })}</p>
              ) : (
                <div className="space-y-3">
                  {Object.entries(byCategory).sort((a, b) => b[1] - a[1]).map(([cat, count]) => {
                    const total = analytics?.total_items || 1;
                    const pct = Math.round((count / total) * 100);
                    return (
                      <div key={cat}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium text-gray-700">{cat}</span>
                          <span className="text-gray-500">{count} ({pct}%)</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-party-green rounded-full" style={{ width: `${pct}%` }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Uploads */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t({ en: 'Recent Uploads', bn: 'সাম্প্রতিক আপলোড' })}</CardTitle>
          </CardHeader>
          <CardContent>
            {(!analytics?.recent_uploads || analytics.recent_uploads.length === 0) ? (
              <p className="text-gray-400 text-center py-8">{t({ en: 'No recent uploads', bn: 'কোনো সাম্প্রতিক আপলোড নেই' })}</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {analytics.recent_uploads.map((item) => (
                  <div key={item._id} className="group relative aspect-square bg-gray-100 rounded-lg overflow-hidden">
                    {item.media_type === 'image' || !item.media_type ? (
                      <img src={item.url} alt={item.caption?.en || ''} className="w-full h-full object-cover" />
                    ) : item.media_type === 'video' ? (
                      <div className="w-full h-full flex items-center justify-center bg-purple-50">
                        <Video className="h-8 w-8 text-purple-400" />
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-orange-50">
                        <Music className="h-8 w-8 text-orange-400" />
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-xs text-white truncate">{item.caption?.en || item.original_filename || 'Media'}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default MediaAnalytics;
